import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { fetchUpdateUserPaymentDetails } from './service';
import { AxiosError, AxiosInstance } from 'axios';
import { UserPaymentDetailsQueryKeys } from '../../queryKeys';
import { GenericError, UserPaymentDetails } from '@starsoft/common/models';
import { UpdateUserPaymentDetailsInputDto } from '@starsoft/common/dto';
import {
  useAxiosInstance,
  useWithdrawSearchParams,
} from '@starsoft/common/hooks';

/**
 * Custom hook to update user payment details
 *
 * This hook handles updating existing payment details for a user, managing the mutation state,
 * and handling side effects like cache invalidation and URL param cleanup after successful update.
 * It uses React Query's mutation capabilities for state management and caching.
 *
 * @returns {Object} An object containing:
 *   - isPending: Boolean indicating if mutation is in progress
 *   - updateUserPaymentDetails: Function to trigger the update mutation
 *     @param {UpdateUserPaymentDetailsInputDto} data - The payment details data to update
 *     @returns {Promise<UserPaymentDetails>} The updated payment details
 */
export function useUpdateUserPaymentDetails() {
  const queryClient: QueryClient = useQueryClient();
  const { handleDeleteWithdrawSearchParams } = useWithdrawSearchParams();
  const axiosInstance: AxiosInstance = useAxiosInstance();

  /**
   * Handles side effects after successful payment details update
   * - Invalidates relevant payment details queries to trigger refetch
   * - Removes withdraw-related URL parameters
   */
  function onSuccess() {
    queryClient.invalidateQueries({
      queryKey: [UserPaymentDetailsQueryKeys.MeInfinity],
    });
    queryClient.invalidateQueries({
      queryKey: [UserPaymentDetailsQueryKeys.MeDefault],
    });
    handleDeleteWithdrawSearchParams('user-payment');
  }

  const { isPending, mutate } = useMutation<
    UserPaymentDetails,
    AxiosError<GenericError>,
    UpdateUserPaymentDetailsInputDto
  >({
    mutationFn: (data: UpdateUserPaymentDetailsInputDto) =>
      fetchUpdateUserPaymentDetails(data, axiosInstance),
    onSuccess,
  });

  return {
    isPending,
    updateUserPaymentDetails: mutate,
  };
}
